import React, { useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface InstallmentRecord {
  id: string;
  installmentNumber: number | null;
  amountCents: number;
  currency: string;
  dueAt: string | null;
  paidAt: string | null;
  status: string;
  stripeInvoiceId?: string | null;
}

interface TransferRecord {
  id: string;
  amountCents: number;
  status: string;
  createdAt: string;
  stripeTransferId: string | null;
  transferType?: string | null;
}

interface OrderSummary {
  orderId: string;
  packageName: string;
  clientName: string;
  totalCents: number;
  currency: string;
  status: string;
  createdAt: string;
  installmentsTotal: number | null;
  installmentsPaid: number | null;
  nextInvoiceAt: string | null;
  scheduleId: string | null;
  subscriptionId: string | null;
  depositCents: number;
  durationMonths: number | null;
  installments: InstallmentRecord[];
  transfers: TransferRecord[];
}

interface Payment {
  id: string;
  client_name: string;
  package_name: string;
  payment_date: string;
  payment_amount: number;
  status: string;
  payment_type?: 'deposit' | 'installment' | 'full';
  installment_number?: number | null;
  total_installments?: number | null;
  order_summary: OrderSummary;
}

interface PaymentDetailsModalProps {
  payment: Payment;
  onClose: () => void;
}

interface TimelineEntry {
  id: string;
  label: string;
  amountCents: number;
  currency: string;
  status: string;
  date: string | null;
  note?: string;
}

const formatCurrency = (cents: number | null | undefined, currency = 'USD') => {
  const safeCents = Number.isFinite(cents as number) ? (cents as number) : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(safeCents / 100);
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const statusBadgeConfig: Record<string, { label: string; className: string }> = {
  paid: { label: 'Paid', className: 'bg-[#2C2731] text-[#FFFCEF] border border-[#2C2731]' },
  succeeded: { label: 'Paid', className: 'bg-[#2C2731] text-[#FFFCEF] border border-[#2C2731]' },
  paid_pending_delivery: { label: 'Paid', className: 'bg-[#2C2731] text-[#FFFCEF] border border-[#2C2731]' },
  delivered: { label: 'Delivered', className: 'bg-[#F3EBCD] text-[#2C2731] border border-[#2C2731]/40' },
  pending_payment: { label: 'Pending', className: 'bg-[#E8DFBD] text-[#2C2731] border border-[#2C2731]' },
  requires_payment_method: { label: 'Pending', className: 'bg-[#E8DFBD] text-[#2C2731] border border-[#2C2731]' },
  scheduled: { label: 'Scheduled', className: 'bg-[#FFFCEF] text-[#2C2731] border border-[#2C2731]/60' },
  active: { label: 'Scheduled', className: 'bg-[#FFFCEF] text-[#2C2731] border border-[#2C2731]/60' },
  cancelled: { label: 'Cancelled', className: 'bg-[#2C2731]/10 text-[#2C2731]/80 border border-[#2C2731]/30' },
  canceled: { label: 'Cancelled', className: 'bg-[#2C2731]/10 text-[#2C2731]/80 border border-[#2C2731]/30' },
  refunded: { label: 'Refunded', className: 'bg-[#2C2731]/15 text-[#2C2731] border border-[#2C2731]/30' },
};

const transferStatusConfig: Record<string, { label: string; className: string }> = {
  queued: { label: 'Queued', className: 'bg-[#F9F1CC] text-[#2C2731]' },
  succeeded: { label: 'Cleared', className: 'bg-[#304031] text-[#FFFCEF]' },
  reversed: { label: 'Reversed', className: 'bg-[#FCEDEA] text-[#8C2F2F]' },
  failed: { label: 'Failed', className: 'bg-[#FCEDEA] text-[#8C2F2F]' },
};

const formatPaymentTypeLabel = (payment: Payment, totalInstallments?: number | null) => {
  if (payment.payment_type === 'deposit') return 'Deposit';
  if (payment.payment_type === 'installment') {
    if (totalInstallments && payment.installment_number != null) {
      return `Installment ${payment.installment_number}/${totalInstallments}`;
    }
    return 'Installment';
  }
  if (payment.payment_type === 'full') return 'Paid in full';
  if (totalInstallments) {
    return `${totalInstallments} payments`;
  }
  return 'One-time payment';
};

const normalizeInstallmentStatus = (status?: string | null) => {
  if (!status) return 'scheduled';
  const normalized = status.toLowerCase();
  if (['paid', 'succeeded', 'completed'].includes(normalized)) return 'paid';
  if (['scheduled', 'pending', 'pending_payment', 'requires_payment_method', 'active'].includes(normalized)) {
    return 'scheduled';
  }
  if (['processing', 'in_progress'].includes(normalized)) return 'processing';
  if (['failed', 'canceled', 'cancelled', 'overdue'].includes(normalized)) return 'failed';
  return normalized;
};

const installmentStatusConfig: Record<string, { label: string; className: string }> = {
  paid: { label: 'Paid', className: 'bg-[#304031] text-[#FFFCEF]' },
  scheduled: { label: 'Scheduled', className: 'bg-[#F3EBCD] text-[#2C2731]' },
  processing: { label: 'Processing', className: 'bg-[#E8DFBD] text-[#2C2731]' },
  failed: { label: 'Failed', className: 'bg-[#FCEDEA] text-[#8C2F2F]' },
};

export const PaymentDetailsModal: React.FC<PaymentDetailsModalProps> = ({ payment, onClose }) => {
  const orderSummary = payment.order_summary;
  if (!orderSummary) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#2C2731]/80 px-4 py-8">
        <div className="rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-6 text-center text-[#2C2731] shadow-xl">
          <p className="font-semibold">Payment details unavailable.</p>
          <button
            type="button"
            onClick={onClose}
            className="mt-4 inline-flex items-center rounded-full border border-[#2C2731]/40 bg-[#FFFCEF] px-6 py-3 text-sm font-semibold text-[#2C2731]"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const transfers = (orderSummary.transfers ?? []).filter(
    (transfer) => (transfer.transferType || 'order') === 'order'
  );
  const installments = orderSummary.installments ?? [];

  const totalCents = orderSummary.totalCents ?? Math.round(payment.payment_amount * 100);
  const currency = (orderSummary.currency || 'USD').toUpperCase();
  const packageName = orderSummary.packageName ?? payment.package_name;

  const paidStatusSet = useMemo(
    () =>
      new Set([
        'paid_pending_delivery',
        'paid',
        'succeeded',
        'delivered',
        'refunded',
      ]),
    []
  );

  const depositCents = orderSummary.depositCents ?? Math.max(
    totalCents - installments.reduce((sum, inst) => sum + inst.amountCents, 0),
    0
  );
  const depositPaid = paidStatusSet.has((orderSummary.status || '').toLowerCase());
  const depositPaidCents = depositCents && depositPaid ? depositCents : 0;
  const orderPaid = depositPaid;

  const installmentsPaidCents = installments
    .filter((inst) => inst.status === 'paid')
    .reduce((sum, inst) => sum + inst.amountCents, 0);

  const paidCents = depositPaidCents + installmentsPaidCents;
  const remainingCents = Math.max(totalCents - paidCents, 0);
  const progressPercent = totalCents > 0 ? Math.min(100, (paidCents / totalCents) * 100) : 0;

  const totalInstallments =
    orderSummary.installmentsTotal ??
    payment.total_installments ??
    (installments.length > 0 ? installments.length : null);

  const installmentsPaidCount =
    typeof orderSummary.installmentsPaid === 'number'
      ? orderSummary.installmentsPaid
      : installments.filter((inst) => inst.status === 'paid').length;
  const remainingInstallments = totalInstallments
    ? Math.max(totalInstallments - installmentsPaidCount, 0)
    : 0;

  const nextScheduledInstallment = installments.find((inst) => inst.status !== 'paid');
  const nextInvoiceDate = nextScheduledInstallment?.dueAt
    ? new Date(nextScheduledInstallment.dueAt)
    : orderSummary.nextInvoiceAt
    ? new Date(orderSummary.nextInvoiceAt)
    : null;

  const sortedInstallments = useMemo(() => {
    return [...installments].sort((a, b) => {
      if (a.installmentNumber != null && b.installmentNumber != null) {
        return a.installmentNumber - b.installmentNumber;
      }
      if (a.dueAt && b.dueAt) {
        return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
      }
      return a.id.localeCompare(b.id);
    });
  }, [installments]);

  const paymentTimelineEntries = useMemo<TimelineEntry[]>(() => {
    const entries: TimelineEntry[] = [];
    if (depositCents > 0) {
      entries.push({
        id: `${payment.id}-deposit`,
        label: 'Deposit',
        amountCents: depositCents,
        currency,
        status: depositPaid ? 'paid' : 'scheduled',
        date: orderSummary.createdAt ?? payment.payment_date,
      });
    }
    sortedInstallments.forEach((inst, index) => {
      entries.push({
        id: inst.id,
        label: `Installment ${inst.installmentNumber ?? index + 1}`,
        amountCents: inst.amountCents,
        currency: inst.currency || currency,
        status: inst.status,
        date: inst.status === 'paid' ? inst.paidAt : inst.dueAt,
      });
    });
    return entries;
  }, [depositCents, depositPaid, payment.id, payment.payment_date, sortedInstallments, currency, orderSummary.createdAt]);

  const fallbackPaidCents = depositPaidCents > 0 ? depositPaidCents : installmentsPaidCents;

  const createdAtDisplay = orderSummary.createdAt
    ? formatDate(orderSummary.createdAt)
    : payment.payment_date;
  const paymentTypeLabel = formatPaymentTypeLabel(payment, totalInstallments);

  const statusChip = statusBadgeConfig[payment.status] || statusBadgeConfig.pending_payment;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#2C2731]/80 px-4 py-8">
      <div
        className="absolute inset-0"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-5xl" role="dialog" aria-modal="true" aria-label="Payment details">
        <div className="flex max-h-[90vh] flex-col overflow-hidden rounded-3xl border border-[#2C2731] bg-[#FFFCEF] shadow-[0_32px_80px_-30px_rgba(32,24,44,0.65)]">
          <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[#FFFCEF]/30 text-xl text-[#FFFCEF] transition hover:bg-[#FFFCEF]/10"
              aria-label="Close payment details modal"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.png" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>

          <main className="flex flex-1 flex-col gap-6 overflow-y-auto px-6 pb-6 pt-6 md:flex-row">
              <>
                <section className="space-y-4 text-[#214D3C] md:w-[45%]">
                  <div className="rounded-[28px] border border-[#304031]/20 bg-[#FFF8E1]/90 p-5 shadow-[0_30px_60px_-40px_rgba(32,77,60,0.45)]">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#214D3C]/70">
                      Payment overview
                    </p>
                    <h2 className="mt-1 font-recoleta text-[28px] font-normal leading-tight text-[#304031]">
                      {payment.client_name}
                    </h2>
                    <p className="mt-1 text-sm">
                      Package: <span className="font-semibold">{packageName}</span>
                    </p>

                    <dl className="mt-4 space-y-3 text-sm">
                      <div className="flex items-center justify-between">
                        <dt className="text-[#214D3C]/70">Order date</dt>
                        <dd className="font-semibold text-[#304031]">{createdAtDisplay}</dd>
                      </div>
                      <div className="flex items-center justify-between">
                        <dt className="text-[#214D3C]/70">Payment type</dt>
                        <dd className="font-semibold text-[#304031]">{paymentTypeLabel}</dd>
                      </div>
                      <div className="flex items-center justify-between">
                        <dt className="text-[#214D3C]/70">Status</dt>
                        <dd>
                          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusChip.className}`}>
                            {statusChip.label}
                          </span>
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <div className="rounded-[28px] border border-[#304031]/20 bg-white/90 p-5 shadow-[0_20px_50px_-35px_rgba(12,10,20,0.6)]">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#214D3C]/70">
                          Stripe payments
                        </p>
                        <h3 className="mt-1 text-xl font-semibold text-[#304031]">
                          {transfers.length > 0 ? 'Received funds' : 'Pending funds'}
                        </h3>
                      </div>
                      <span className="rounded-full bg-[#FFFCEF] px-3 py-1 text-xs font-medium text-[#304031]">
                        {transfers.length} payment{transfers.length === 1 ? '' : 's'}
                      </span>
                    </div>

                    {transfers.length === 0 ? (
                      orderPaid ? (
                        <div className="mt-4 rounded-2xl border border-[#304031]/20 bg-[#FFFCEF] px-4 py-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-semibold text-[#304031]">Stripe payment</p>
                              <p className="text-xs text-[#214D3C]/70">{payment.payment_date}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-semibold text-[#304031]">
                                {formatCurrency(fallbackPaidCents, currency)}
                              </p>
                              <span className="mt-1 inline-flex items-center rounded-full bg-[#304031] px-2.5 py-0.5 text-[11px] font-semibold text-[#FFFCEF]">
                                Captured
                              </span>
                            </div>
                          </div>
                          <p className="mt-2 text-xs text-[#214D3C]/70">
                            Transfer to your account is processing; funds typically settle within 1–2 business days.
                          </p>
                        </div>
                      ) : (
                        <div className="mt-6 rounded-2xl border border-dashed border-[#304031]/20 bg-[#FFFCEF] px-4 py-6 text-center text-sm text-[#214D3C]/80">
                          No Stripe transfers have been recorded for this order yet.
                        </div>
                      )
                    ) : (
                      <div className="mt-4 space-y-3">
                        {transfers.map((transfer, index) => {
                          const transferChip =
                            transferStatusConfig[transfer.status] || transferStatusConfig.queued;
                          return (
                            <div
                              key={transfer.id}
                              className="flex items-center justify-between rounded-2xl border border-[#304031]/15 bg-[#FFFCEF] px-4 py-3"
                            >
                              <div>
                                <p className="text-sm font-semibold text-[#304031]">
                                  Transfer {index + 1}
                                </p>
                                <p className="text-xs text-[#214D3C]/70">{formatDate(transfer.createdAt)}</p>
                                {transfer.stripeTransferId && (
                                  <p className="text-[11px] text-[#214D3C]/60">
                                    ID · …{transfer.stripeTransferId.slice(-8)}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-semibold text-[#304031]">
                                  {formatCurrency(transfer.amountCents, currency)}
                                </p>
                                <span
                                  className={`mt-1 inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${transferChip.className}`}
                                >
                                  {transferChip.label}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </section>

                <section className="flex-1 space-y-4">
                  <div className="rounded-2xl border border-[#304031]/15 bg-white/90 p-5 shadow-[0_30px_60px_-40px_rgba(15,8,27,0.45)]">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#214D3C]/70">
                          Total package value
                        </p>
                        <p className="mt-1 text-3xl font-semibold text-[#304031]">
                          {formatCurrency(totalCents, currency)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs uppercase tracking-[0.12em] text-[#214D3C]/70">Received</p>
                        <p className="mt-1 text-xl font-semibold text-[#214D3C]">
                          {formatCurrency(paidCents, currency)}
                        </p>
                        <p className="text-xs text-[#214D3C]/70">
                          Outstanding: <span className="font-semibold">{formatCurrency(remainingCents, currency)}</span>
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 h-2 rounded-full bg-[#E8DFBD]">
                      <div
                        className="h-full rounded-full bg-[#304031] transition-all"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-[#214D3C]/80">
                      <span>{progressPercent.toFixed(0)}% collected</span>
                      {remainingCents > 0 && (
                        <span>
                          {totalInstallments
                            ? `${remainingInstallments} payments remaining`
                            : 'Awaiting remaining funds'}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-[#304031]/15 bg-[#FFFCEF] p-5">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#214D3C]/70">
                          Installment plan
                        </p>
                        <p className="mt-1 text-2xl font-semibold text-[#304031]">
                          {totalInstallments
                            ? `${Math.min(installmentsPaidCount, totalInstallments)} / ${totalInstallments}`
                            : `${installmentsPaidCount} payment${installmentsPaidCount === 1 ? '' : 's'}`}
                        </p>
                        <p className="text-sm text-[#214D3C]/80">payments completed</p>
                      </div>
                      {nextInvoiceDate && remainingCents > 0 && (
                        <div className="rounded-2xl border border-[#304031]/25 bg-[#304031] px-4 py-3 text-center text-sm text-[#FFFCEF] shadow-[0_10px_25px_-15px_rgba(17,24,39,0.9)]">
                          <p className="text-[11px] uppercase tracking-[0.2em] text-[#FFFCEF]/70">Next Payment</p>
                          <p className="mt-1 text-base font-semibold">{formatDate(nextInvoiceDate.toISOString())}</p>
                        </div>
                      )}
                    </div>

                    {paymentTimelineEntries.length > 0 ? (
                      <div className="mt-5 space-y-2">
                        {paymentTimelineEntries.map((entry) => {
                          const chip = installmentStatusConfig[entry.status] || installmentStatusConfig.scheduled;
                          const dateLabel = entry.date
                            ? entry.status === 'paid'
                              ? `Paid ${formatDate(entry.date)}`
                              : `Due ${formatDate(entry.date)}`
                            : entry.status === 'paid'
                            ? 'Paid'
                            : 'Date TBD';
                          return (
                            <div
                              key={entry.id}
                              className="flex items-center justify-between rounded-xl border border-[#304031]/15 bg-white px-4 py-3 text-sm text-[#214D3C]"
                            >
                              <div>
                                <p className="font-semibold text-[#304031]">{entry.label}</p>
                                <p className="text-xs text-[#214D3C]/70">{dateLabel}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-[#304031]">
                                  {formatCurrency(entry.amountCents, entry.currency || currency)}
                                </p>
                                <span
                                  className={`mt-1 inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${chip.className}`}
                                >
                                  {chip.label}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="mt-4 text-sm text-[#214D3C]/80">
                        {remainingCents === 0
                          ? 'All scheduled payments have been received.'
                          : 'No installments are associated with this order yet.'}
                      </p>
                    )}

                    {orderSummary.scheduleId && (
                      <div className="mt-5 rounded-xl border border-dashed border-[#304031]/30 bg-white/70 px-4 py-3 text-xs text-[#214D3C]">
                        Subscription schedule ID:{' '}
                        <span className="font-semibold text-[#304031]">{orderSummary.scheduleId}</span>
                      </div>
                    )}
                  </div>
                </section>
              </>
          </main>

          <footer className="flex items-center justify-end border-t border-[#FFFCEF]/25 bg-[#FFFCEF] px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center rounded-full border border-[#304031]/40 bg-[#FFFCEF] px-6 py-3 text-sm font-semibold text-[#304031] transition hover:-translate-y-0.5 hover:border-[#304031]"
            >
              Close
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetailsModal;
